import { v4 as uuidv4 } from "uuid";
import EndPoints from "./api-endpoints";
import { BASE_URL } from "./config";
import SessionManager from "./session-manager";
import type { NotificationItem } from "../store/useNotificationStore";

type NotificationStreamOptions = {
  recipientUserId?: string;
  recipientEmail?: string;
  onConnected?: (message: string) => void;
  onNotification?: (notification: NotificationItem) => void;
  onConnectionStateChange?: (isConnected: boolean) => void;
  onError?: (error: unknown) => void;
};

const RECONNECT_DELAY_MILLIS = 3000;

const parseEventBlock = (
  rawBlock: string,
  options: NotificationStreamOptions,
) => {
  const lines = rawBlock.split(/\r?\n/);
  let eventName = "message";
  const dataLines: string[] = [];

  lines.forEach((line) => {
    if (line.startsWith("event:")) {
      eventName = line.slice(6).trim();
      return;
    }

    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trim());
    }
  });

  const dataText = dataLines.join("\n").trim();
  if (!dataText) {
    return;
  }

  if (eventName === "connected") {
    options.onConnected?.(dataText);
    return;
  }

  if (eventName === "notification") {
    try {
      options.onNotification?.(JSON.parse(dataText) as NotificationItem);
    } catch (error) {
      options.onError?.(error);
    }
  }
};

export const connectNotificationStream = (
  options: NotificationStreamOptions,
) => {
  const sessionManager = new SessionManager();
  const params = new URLSearchParams();
  if (options.recipientUserId) {
    params.set("recipientUserId", options.recipientUserId);
  }
  if (options.recipientEmail) {
    params.set("recipientEmail", options.recipientEmail);
  }

  let disposed = false;
  let reconnectTimer: number | null = null;
  let abortController: AbortController | null = null;

  const clearReconnectTimer = () => {
    if (reconnectTimer) {
      window.clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  const scheduleReconnect = () => {
    if (disposed || reconnectTimer) {
      return;
    }

    reconnectTimer = window.setTimeout(() => {
      reconnectTimer = null;
      void startStream();
    }, RECONNECT_DELAY_MILLIS);
  };

  const startStream = async () => {
    if (disposed) {
      return;
    }

    abortController = new AbortController();
    options.onConnectionStateChange?.(false);

    try {
      const token = await sessionManager.getAccessToken();
      const response = await fetch(
        `${BASE_URL}${EndPoints.streamNotifications}?${params.toString()}`,
        {
          method: "GET",
          headers: {
            Accept: "text/event-stream",
            "correlation-id": uuidv4(),
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          signal: abortController.signal,
        },
      );

      if (!response.ok || !response.body) {
        throw new Error(
          `Notification stream failed with status ${response.status}`,
        );
      }

      options.onConnectionStateChange?.(true);
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";

      while (!disposed) {
        const { done, value } = await reader.read();
        if (done) {
          break;
        }

        buffer += decoder.decode(value, { stream: true });
        const eventBlocks = buffer.split(/\r?\n\r?\n/);
        buffer = eventBlocks.pop() ?? "";

        eventBlocks.forEach((eventBlock) =>
          parseEventBlock(eventBlock, options),
        );
      }
    } catch (error) {
      if (!disposed) {
        options.onError?.(error);
      }
    } finally {
      options.onConnectionStateChange?.(false);
      if (!disposed) {
        scheduleReconnect();
      }
    }
  };

  void startStream();

  return () => {
    disposed = true;
    clearReconnectTimer();
    options.onConnectionStateChange?.(false);
    abortController?.abort();
  };
};
